/**
 * Webhook Settings Component
 *
 * Configure and test webhook integrations for form submissions.
 */

import { useState, useEffect } from 'react';
import { useBuilderStore } from '../../stores/builderStore';
import type { WebhookAction, SubmitAction } from '../../types/form';

interface WebhookSettingsProps {
  formId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface TestResult {
  success: boolean;
  statusCode?: number;
  statusText?: string;
  responseBody?: string;
  responseTime?: number;
  error?: string;
}

export default function WebhookSettings({ formId, isOpen, onClose }: WebhookSettingsProps) {
  const { formSchema, updateSettings } = useBuilderStore();

  // Get existing webhook action from settings
  const existingWebhook = formSchema.settings.submitActions?.find(
    (action): action is WebhookAction => action.type === 'webhook'
  );

  const [webhook, setWebhook] = useState<WebhookAction>({
    type: 'webhook',
    url: existingWebhook?.url || '',
    method: existingWebhook?.method || 'POST',
    headers: existingWebhook?.headers || {},
  });

  const [newHeaderKey, setNewHeaderKey] = useState('');
  const [newHeaderValue, setNewHeaderValue] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  // Update local state when schema changes
  useEffect(() => {
    const existing = formSchema.settings.submitActions?.find(
      (action): action is WebhookAction => action.type === 'webhook'
    );
    if (existing) {
      setWebhook(existing);
    }
  }, [formSchema.settings.submitActions]);

  const handleSave = () => {
    // Get current submit actions without webhooks
    const otherActions =
      formSchema.settings.submitActions?.filter((a) => a.type !== 'webhook') || [];

    // Add webhook if URL is provided
    let newActions: SubmitAction[] = otherActions;
    if (webhook.url.trim()) {
      newActions = [...otherActions, webhook];
    }

    updateSettings({ submitActions: newActions });
    onClose();
  };

  const addHeader = () => {
    if (newHeaderKey.trim() && newHeaderValue.trim()) {
      setWebhook({
        ...webhook,
        headers: {
          ...webhook.headers,
          [newHeaderKey.trim()]: newHeaderValue.trim(),
        },
      });
      setNewHeaderKey('');
      setNewHeaderValue('');
    }
  };

  const removeHeader = (key: string) => {
    const newHeaders = { ...webhook.headers };
    delete newHeaders[key];
    setWebhook({ ...webhook, headers: newHeaders });
  };

  const testWebhook = async () => {
    if (!webhook.url) {
      setTestResult({ success: false, error: 'Please enter a webhook URL' });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const response = await fetch(`/api/forms/${formId}/test-webhook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ webhook }),
      });

      const result = await response.json();
      setTestResult(result as TestResult);
    } catch (error) {
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to test webhook',
      });
    } finally {
      setTesting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-xl">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Webhook Settings</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Configure a webhook to receive form submissions
            </p>
          </div>

          <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
            {/* Webhook URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Webhook URL
              </label>
              <input
                type="url"
                value={webhook.url}
                onChange={(e) => setWebhook({ ...webhook, url: e.target.value })}
                placeholder="https://your-server.com/webhook"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                The URL that will receive POST requests when forms are submitted
              </p>
            </div>

            {/* HTTP Method */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                HTTP Method
              </label>
              <select
                value={webhook.method}
                onChange={(e) =>
                  setWebhook({ ...webhook, method: e.target.value as 'POST' | 'PUT' })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
              </select>
            </div>

            {/* Custom Headers */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Headers
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Add authentication tokens or custom headers
              </p>

              {/* Existing headers */}
              {Object.entries(webhook.headers || {}).length > 0 && (
                <div className="space-y-2 mb-3">
                  {Object.entries(webhook.headers || {}).map(([key, value]) => (
                    <div
                      key={key}
                      className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg"
                    >
                      <span className="text-sm font-mono text-gray-700 flex-1">
                        {key}: {value.slice(0, 30)}
                        {value.length > 30 ? '...' : ''}
                      </span>
                      <button
                        onClick={() => removeHeader(key)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new header */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newHeaderKey}
                  onChange={(e) => setNewHeaderKey(e.target.value)}
                  placeholder="Header name"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={newHeaderValue}
                  onChange={(e) => setNewHeaderValue(e.target.value)}
                  placeholder="Value"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={addHeader}
                  disabled={!newHeaderKey.trim() || !newHeaderValue.trim()}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Test Webhook */}
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Test Webhook</h3>
                  <p className="text-xs text-gray-500">
                    Send a test request to verify your webhook configuration
                  </p>
                </div>
                <button
                  onClick={testWebhook}
                  disabled={testing || !webhook.url}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testing ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Testing...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Send Test
                    </>
                  )}
                </button>
              </div>

              {/* Test Result */}
              {testResult && (
                <div
                  className={`p-4 rounded-lg ${
                    testResult.success
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-red-50 border border-red-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {testResult.success ? (
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`font-medium ${
                          testResult.success ? 'text-green-800' : 'text-red-800'
                        }`}
                      >
                        {testResult.success ? 'Webhook test successful!' : 'Webhook test failed'}
                      </p>

                      {testResult.statusCode && (
                        <p className="text-sm text-gray-600 mt-1">
                          Status: {testResult.statusCode} {testResult.statusText}
                          {testResult.responseTime && (
                            <span className="ml-2">({testResult.responseTime}ms)</span>
                          )}
                        </p>
                      )}

                      {testResult.error && (
                        <p className="text-sm text-red-600 mt-1">{testResult.error}</p>
                      )}

                      {testResult.responseBody && (
                        <details className="mt-2">
                          <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                            View response body
                          </summary>
                          <pre className="mt-2 p-2 bg-white rounded text-xs overflow-x-auto max-h-32 overflow-y-auto">
                            {testResult.responseBody}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Payload Preview */}
            <details className="border-t pt-6">
              <summary className="text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900">
                View payload format
              </summary>
              <pre className="mt-3 p-3 bg-gray-50 rounded-lg text-xs overflow-x-auto">
{`{
  "event": "submission",
  "formId": "${formId}",
  "submissionId": "uuid",
  "timestamp": "2025-01-01T00:00:00.000Z",
  "data": {
    "field_name": "field_value",
    ...
  }
}`}
              </pre>
            </details>
          </div>

          <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
